import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { apiService, type UserProfile, type ApiError } from '@/services/api'

/**
 * Sesión del usuario, compartida por toda la aplicación.
 *
 * Antes esto era un hook suelto: cada componente que llamaba `useAuth()` creaba
 * su propio estado y pedía el perfil por su cuenta. Con 43 componentes usándolo
 * eso significaba 43 peticiones y, peor, 43 ventanas de carga distintas: al
 * entrar a una pantalla su instancia arrancaba con el usuario en null, así que
 * por un momento no se veía quién estaba dentro ni el menú, y cualquier efecto
 * que dependiera de `user` se disparaba dos veces.
 *
 * Ahora hay una sola instancia en la raíz y todos leen de ella. La forma de
 * usarlo no cambió: se sigue llamando `useAuth()`.
 */

interface AuthState {
  isAuthenticated: boolean
  user: UserProfile | null
  isLoading: boolean
  initializing: boolean
  error: string | null
  login: (
    email: string,
    password: string,
    accessModule?: 'almacen' | 'rh' | 'proveedores',
  ) => Promise<UserProfile>
  logout: () => void
  updateUserModule: (moduleId: string) => void
}

const AuthContext = createContext<AuthState | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  // A diferencia de isLoading (que también se activa en cada login/logout),
  // initializing solo cubre la verificación inicial del token guardado. App.tsx
  // usa initializing para el gate de pantalla completa: si usara isLoading, cada
  // intento de login (fallido o no) desmontaría toda la pantalla de login mientras
  // dura el request, perdiendo el formulario y cualquier mensaje de error inline.
  const [initializing, setInitializing] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check if user is already logged in and get profile
  useEffect(() => {
    const checkAuth = async () => {
      const token = apiService.getToken()

      if (!token) {
        setIsLoading(false)
        setInitializing(false)
        return
      }

      try {
        const userProfile = await apiService.getProfile()

        // Permitir acceso tanto a usuarios regulares como a admins
        setUser(userProfile)
        setIsAuthenticated(true)
      } catch (err) {
        const apiError = err as ApiError
        console.error('Auth check failed:', apiError)

        // Token might be invalid, clear it
        apiService.removeToken()
        setIsAuthenticated(false)
        setUser(null)

        if (apiError.statusCode === 401) {
          setError('Session expired. Please login again.')
        }
      } finally {
        setIsLoading(false)
        setInitializing(false)
      }
    }

    checkAuth()

    // 🔐 Listener para eventos de sesión no autorizada
    const handleUnauthorized = (event: Event) => {
      const customEvent = event as CustomEvent

      // El portal del proveedor tiene su propia sesión: un 401 suyo no debe
      // sacar al usuario interno. Sin `sesion` se asume interna, que es de
      // donde vienen los avisos de los servicios que no la mandan.
      if (customEvent.detail?.sesion === 'proveedor') return

      console.error('🚨 Evento de sesión no autorizada detectado:', customEvent.detail)

      // Limpiar estado de autenticación
      apiService.removeToken()
      setIsAuthenticated(false)
      setUser(null)
      setError(customEvent.detail?.message || 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.')
    }

    // 🔄 Listener para actualizaciones del perfil del usuario
    const handleProfileUpdated = (event: Event) => {
      const customEvent = event as CustomEvent
      const moduleData = customEvent.detail?.user
      
      if (moduleData?.currentModuleId) {
        // Usar función de callback para acceder al estado más reciente
        setUser(currentUser => {
          if (currentUser) {
            const updatedUser = {
              ...currentUser,
              currentModuleId: moduleData.currentModuleId
            }
            return updatedUser
          }
          return currentUser
        })
      }
    }

    // Agregar listeners para eventos de autenticación
    window.addEventListener('auth:unauthorized', handleUnauthorized)
    window.addEventListener('user:profile:updated', handleProfileUpdated)

    // Cleanup: remover listeners cuando el componente se desmonte
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized)
      window.removeEventListener('user:profile:updated', handleProfileUpdated)
    }
  }, []) // Remover dependencia de 'user' para evitar problemas de closure

  const login = async (
    email: string,
    password: string,
    accessModule?: 'almacen' | 'rh' | 'proveedores',
  ): Promise<UserProfile> => {
    setIsLoading(true)
    setError(null)

    try {
      // First, login to get token
      const loginResponse = await apiService.login({ email, password, accessModule })
      apiService.saveToken(loginResponse.access_token)

      // Then, get user profile
      const userProfile = await apiService.getProfile()

      // Permitir acceso tanto a usuarios regulares como a admins
      setUser(userProfile)
      setIsAuthenticated(true)
      
      // Retornar el perfil del usuario para mostrar mensaje personalizado
      return userProfile
    } catch (err) {
      const apiError = err as ApiError
      apiService.removeToken()
      setIsAuthenticated(false)
      setUser(null)
      throw apiError
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    // Limpiar tokens de sesión RH y proveedor
    apiService.removeToken()
    localStorage.removeItem('supplier_access_token')
    localStorage.removeItem('supplier_data')
    setIsAuthenticated(false)
    setUser(null)
    setError(null)
  }

  const updateUserModule = (moduleId: string) => {
    if (user) {
      setUser({ ...user, currentModuleId: moduleId })
    }
  }

  const valor: AuthState = {
    isAuthenticated,
    user,
    isLoading,
    initializing,
    error,
    login,
    logout,
    updateUserModule,
  }

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth se usó fuera de AuthProvider')
  }
  return ctx
}
